import { useState, useRef, useCallback, useEffect } from 'react'
import { useSignUp, useAuth, useUser, useClerk } from '@clerk/clerk-react'
import { isClerkAPIResponseError } from '@clerk/clerk-react/errors'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { LanguageSelector } from '@/components/auth/language-selector'
import { BrandLogo } from '@/components/branding/brand-logo'
import { useEffectiveBranding } from '@/hooks/use-effective-branding'
import { getPostAuthOrgPath } from '@/hooks/use-privileged-staff-bypass'
import { useBrandTheme } from '@/hooks/use-brand-theme'
import { capture } from '@tagadapay/analytics'
import {
	buildSignUpUnsafeMetadata,
	useAcquisitionTracking,
} from '@/hooks/use-acquisition-tracking'
import { getStoredAcquisition, hasAcquisitionData } from '@/lib/utils/acquisition-tracking'
import {
	captureAndPersistPartnerFromUrl,
	clearCapturedPartner,
	resolveSignupPartner,
} from '@/lib/utils/captured-partner'
import { saveOnboardingIntent, getOnboardingState } from '@/lib/onboarding'
import { useSignupPersistence } from '../hooks/use-signup-persistence'
import { StepProgressBar } from './step-progress-bar'
import { StepAboutYou } from './step-about-you'
import { StepAboutBusiness } from './step-about-business'
import { StepCreateAccount } from './step-create-account'
import { OtpVerification } from './otp-verification'
import type {
	WizardStep,
	StepAboutYouData,
	StepAboutBusinessData,
	StepCreateAccountData,
	SignUpFormData,
} from '../types'
import { buildSignupProfileInput, buildAccountName } from '../utils'
import { useCompleteHubSignup } from '@/lib/api/signupProfile'
import { trackXSignup } from '@/lib/analytics/x-pixel'
import { setGaIdentityFromSignup, trackGaSignUp } from '@/lib/analytics/ga'
import { currentPartnerBrand } from '@/config/current-partner'

const CAPTCHA_ERROR_CODES = ['captcha_invalid', 'captcha_unavailable', 'captcha_failed', 'form_captcha_invalid']

const isCaptchaError = (err: unknown): boolean => {
	if (!isClerkAPIResponseError(err)) return false
	return err.errors.some((e) => CAPTCHA_ERROR_CODES.includes(e.code))
}

const contentVariants = {
	enter: (dir: number) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
	center: { x: 0, opacity: 1 },
	exit: (dir: number) => ({ x: dir > 0 ? -60 : 60, opacity: 0 }),
}

const STEP_LABELS_KEYS = [
	'signUpNew.steps.aboutYou',
	'signUpNew.steps.aboutBusiness',
	'signUpNew.steps.createAccount',
] as const

/** Builds initial form data, pre-filling from Clerk user in SSO mode. */
const buildInitialFormData = (
	persisted: SignUpFormData,
	isSSOMode: boolean,
	clerkUser: { firstName?: string | null; lastName?: string | null; emailAddresses?: { emailAddress: string }[] } | null | undefined,
): SignUpFormData => {
	if (!isSSOMode || !clerkUser) return persisted
	return {
		...persisted,
		aboutYou: {
			...persisted.aboutYou,
			firstName: clerkUser.firstName ?? persisted.aboutYou.firstName,
			lastName: clerkUser.lastName ?? persisted.aboutYou.lastName,
			email: clerkUser.emailAddresses?.[0]?.emailAddress ?? persisted.aboutYou.email,
		},
	}
}

export function SignUpWizard() {
	const { t } = useTranslation('auth')
	const { signUp, setActive, isLoaded } = useSignUp()
	const { isSignedIn } = useAuth()
	const { user: clerkUser } = useUser()
	const clerk = useClerk()
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const { load, save, clear } = useSignupPersistence()
	const { mutateAsync, isError, reset } = useCompleteHubSignup()
	const brand = useEffectiveBranding()
	useBrandTheme(brand)
	useAcquisitionTracking()

	const isSSOMode = searchParams.get('mode') === 'sso' && isSignedIn === true

	const [formData, setFormData] = useState<SignUpFormData>(
		() => buildInitialFormData(load(), isSSOMode, clerkUser),
	)
	const [step, setStep] = useState<WizardStep>(1)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState('')
	const directionRef = useRef(1)

	useEffect(() => {
		if (isSSOMode) return
		if (isSignedIn && step !== 'otp') navigate('/')
	}, [isSignedIn, navigate, step, isSSOMode])

	useEffect(() => {
		const existing = getOnboardingState()
		if (!existing) {
			saveOnboardingIntent({ type: 'store_create', source: 'direct_signup' })
		}
		// `?partner=<slug>` deep-links (partner-shared sign-up URLs) — persisted
		// to cookie + localStorage so it survives the OAuth bounce and the OTP
		// step, then handed to completeHubSignup below (TAG-2514).
		captureAndPersistPartnerFromUrl()
	}, [])

	const goTo = useCallback(
		(next: WizardStep) => {
			const order: Record<WizardStep, number> = { 1: 0, 2: 1, 3: 2, otp: 3 }
			directionRef.current = order[next] >= order[step] ? 1 : -1
			setStep(next)
			setError('')
		},
		[step],
	)

	const handleStep1Next = useCallback(
		(data: StepAboutYouData) => {
			const next = save({ ...formData, aboutYou: data })
			setFormData(next)
			goTo(2)
		},
		[formData, save, goTo],
	)

	const handleStep2Next = useCallback(
		(data: StepAboutBusinessData) => {
			const next = save({ ...formData, aboutBusiness: data })
			setFormData(next)
			goTo(3)
		},
		[formData, save, goTo],
	)

	/** SSO path: skip Clerk user creation and OTP, call completeHubSignup directly. */
	const handleSSOCreateAccount = useCallback(
		async (data: StepCreateAccountData) => {
			const updated = save({ ...formData, createAccount: data })
			setFormData(updated)
			setLoading(true)
			setError('')

			try {
				const partner = resolveSignupPartner(currentPartnerBrand())
				const res = await mutateAsync({
					accountName: buildAccountName(updated),
					signupProfile: buildSignupProfileInput(updated),
					partner,
					referralCode: updated.createAccount.referralCode || undefined,
				})
				await clerk.setActive({ organization: res.orgId })
				clearCapturedPartner()
				capture('account_created', {
					method: 'google',
					partner_slug: partner,
					has_acquisition: hasAcquisitionData(getStoredAcquisition()),
				})
				await trackXSignup({
					email: updated.aboutYou.email,
					conversionId: res.orgId,
				})
				setGaIdentityFromSignup({
					firstName: updated.aboutYou.firstName,
					lastName: updated.aboutYou.lastName,
					email: updated.aboutYou.email,
					accountName: buildAccountName(updated),
					organization: buildAccountName(updated),
				})
				trackGaSignUp({ method: 'email' })
				clear()
				// Full load: a client navigate keeps the shell's membership list fetched before the org existed, which loops through /no-organization.
				window.location.assign(getPostAuthOrgPath(res.orgSlug, false))
			} catch (err: any) {
				setError(
					err.errors?.[0]?.message || err.message || 'Account creation failed. Please try again.',
				)
			} finally {
				setLoading(false)
			}
		},
		[formData, save, mutateAsync, clerk, clear],
	)

	/** Normal path: create Clerk user and start OTP verification. */
	const handleCreateAccount = useCallback(
		async (data: StepCreateAccountData) => {
			if (!isLoaded) return

			const updated = save({ ...formData, createAccount: data })
			setFormData(updated)
			setLoading(true)
			setError('')

			try {
				// First-touch acquisition + device thumbmarks ride on the signUp so
				// the Clerk user is born with them — before the org exists.
				const result = await signUp.create({
					emailAddress: updated.aboutYou.email,
					firstName: updated.aboutYou.firstName,
					lastName: updated.aboutYou.lastName,
					...(await buildSignUpUnsafeMetadata()),
				})

				if (result.status === 'missing_requirements') {
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					const captchaStatus = (result.verifications as any)?.captcha?.status
					if (captchaStatus && captchaStatus !== 'verified') {
						setError(t('signUpNew.errors.captchaPending'))
						return
					}
				}

				await signUp.prepareEmailAddressVerification({
					strategy: 'email_code',
				})
				goTo('otp')
			} catch (err: any) {
				if (isCaptchaError(err)) {
					setError(t('signUpNew.errors.captchaFailed'))
				} else {
					setError(
						err.errors?.[0]?.message || 'Sign up failed. Please try again.',
					)
				}
			} finally {
				setLoading(false)
			}
		},
		[isLoaded, signUp, formData, save, goTo, t],
	)

	const handleGoogleSignUp = useCallback(async () => {
		if (!isLoaded) return

		setLoading(true)
		setError('')

		try {
			save(formData)
		await signUp.authenticateWithRedirect({
			strategy: 'oauth_google',
			redirectUrl: '/sso-callback',
			redirectUrlComplete: '/sso-callback?complete=1',
			// Acquisition + thumbmarks ride the OAuth bounce (TAG-2514).
			...(await buildSignUpUnsafeMetadata()),
		})
		} catch (err: any) {
			setError(
				err.errors?.[0]?.message || 'Google sign up failed. Please try again.',
			)
			setLoading(false)
		}
	}, [isLoaded, signUp, formData, save])

	const handleVerify = async (code: string) => {
		if (!isLoaded) return

		setLoading(true)
		setError('')

		try {
			const result = await signUp.attemptEmailAddressVerification({ code })
			if (result.status === 'complete' && result.createdSessionId) {
				await setActive({ session: result.createdSessionId })
				const partner = resolveSignupPartner(currentPartnerBrand())
				const res = await mutateAsync({
					accountName: buildAccountName(formData),
					signupProfile: buildSignupProfileInput(formData),
					partner,
					referralCode: formData.createAccount.referralCode || undefined,
				})
				await setActive({ organization: res.orgId, session: result.createdSessionId })
				clearCapturedPartner()
				capture('account_created', {
					method: 'email',
					partner_slug: partner,
					has_acquisition: hasAcquisitionData(getStoredAcquisition()),
				})
				await trackXSignup({
					email: formData.aboutYou.email,
					conversionId: res.orgId,
				})
				setGaIdentityFromSignup({
					firstName: formData.aboutYou.firstName,
					lastName: formData.aboutYou.lastName,
					email: formData.aboutYou.email,
					accountName: buildAccountName(formData),
					organization: buildAccountName(formData),
				})
				trackGaSignUp({ method: 'email' })
				clear()
				// Full load: a client navigate keeps the shell's membership list fetched before the org existed, which loops through /no-organization.
				window.location.assign(getPostAuthOrgPath(res.orgSlug, false))
			} else if (result.status === 'missing_requirements') {
				setError('Signup configuration error. Please contact support.')
			}
		} catch (err: any) {
			const msg =
				err.errors?.[0]?.message || 'Verification failed. Please try again.'
			setError(msg)
		} finally {
			setLoading(false)
		}
	}

	const handleEditStep = useCallback(
		(target: 1 | 2) => goTo(target),
		[goTo],
	)

	if (step === 'otp') {
		return (
			<OtpVerification
				email={formData.aboutYou.email}
				onVerify={handleVerify}
				onBack={() => goTo(3)}
				loading={loading}
				error={error}
			/>
		)
	}

	const stepLabels = STEP_LABELS_KEYS.map((k) => t(k))
	const currentStepNumber = step as number

	return (
		<div className="flex h-[100dvh]">
			{/* Main form column */}
			<div className="flex-1 overflow-y-auto px-4 py-6 sm:px-8 sm:py-8">
				<div className="mx-auto w-full max-w-md sm:my-auto sm:flex sm:min-h-full sm:flex-col sm:justify-center">
					{/* Header: language selector */}
					<div className="mb-3 flex justify-end sm:mb-4">
						<LanguageSelector />
					</div>

					{/* Logo */}
					<div className="mb-4 flex justify-center sm:mb-6">
						<BrandLogo brand={brand} className="h-10 w-auto" />
					</div>

					{/* Step indicator */}
					<div className="mb-6 sm:mb-8">
						<StepProgressBar
							currentStep={currentStepNumber}
							labels={stepLabels}
						/>
					</div>

					{/* Animated step content */}
					<AnimatePresence mode="wait" custom={directionRef.current}>
						{step === 1 && (
							<motion.div
								key="step1"
								custom={directionRef.current}
								variants={contentVariants}
								initial="enter"
								animate="center"
								exit="exit"
								transition={{ duration: 0.25, ease: 'easeInOut' }}
							>
								<StepAboutYou
									defaultValues={formData.aboutYou}
									onNext={handleStep1Next}
									onBack={() => navigate('/sign-in')}
									disabled={loading}
									emailReadOnly={isSSOMode}
								/>
							</motion.div>
						)}

						{step === 2 && (
							<motion.div
								key="step2"
								custom={directionRef.current}
								variants={contentVariants}
								initial="enter"
								animate="center"
								exit="exit"
								transition={{ duration: 0.25, ease: 'easeInOut' }}
							>
								<StepAboutBusiness
									defaultValues={formData.aboutBusiness}
									onNext={handleStep2Next}
									onBack={() => goTo(1)}
									disabled={loading}
								/>
							</motion.div>
						)}

						{step === 3 && (
							<motion.div
								key="step3"
								custom={directionRef.current}
								variants={contentVariants}
								initial="enter"
								animate="center"
								exit="exit"
								transition={{ duration: 0.25, ease: 'easeInOut' }}
							>
								<StepCreateAccount
									aboutYou={formData.aboutYou}
									aboutBusiness={formData.aboutBusiness}
									defaultValues={formData.createAccount}
									onSubmit={isSSOMode ? handleSSOCreateAccount : handleCreateAccount}
									onGoogleSignUp={handleGoogleSignUp}
									onBack={() => goTo(2)}
									onEditStep={handleEditStep}
									loading={loading}
									error={error}
									hideGoogleSignUp={isSSOMode}
								/>
							</motion.div>
						)}
					</AnimatePresence>

					{/* Footer */}
					<p className="text-muted-foreground mt-4 text-center text-sm sm:mt-6">
						{t('signUp.haveAccount')}{' '}
						<Link
							to="/sign-in"
							className="text-foreground hover:text-primary font-semibold underline underline-offset-4"
						>
							{t('signUp.signInLink')}
						</Link>
					</p>
				</div>
			</div>
		</div>
	)
}
